import styled, { css } from 'styled-components';

import { Size as IconSize } from '../../theme/Icon';
import * as BaseSizes from '../../theme/Size';
import type { Position, Size } from '../../types';
import { getFontStyle } from '../../utils';
import { rem } from '../../utils/index';
import { SelectiveButtonProps } from './SelectiveButton';

export const SelectiveButtonSize: Record<string, number> = {
  XSmall: BaseSizes.Large,
  Small: BaseSizes.X2Large,
  Medium: BaseSizes.X3Large,
  Large: BaseSizes.X5Large,
  XLarge: BaseSizes.X7Large,
};

export const getSelectiveButtonSize = ({
  variant = 'default',
  size = 'Medium',
}: {
  variant?: SelectiveButtonProps['variant'];
  size?: SelectiveButtonProps['size'];
}) => {
  const height = SelectiveButtonSize[size] ? rem(SelectiveButtonSize[size]) : 'auto';

  return css`
    height: ${height};
  `;
};

export const getBadgePosition = ({
  position = 'top-right',
  size = 'Small',
}: {
  position?: Position;
  size?: Size;
}) => {
  const shift: string = rem(IconSize[size] / 2);

  return css`
    ${() => {
      switch (position) {
        case 'top-right':
          return `
            right: -${shift};
            top: -${shift};
          `;
        case 'top-left':
          return `
            left: -${shift};
            top: -${shift};
          `;
      }

      return '';
    }}
  `;
};

export const StyledButtonsWrapper = styled.div`
  display: flex;
  width: fit-content;
  position: relative;
`;

export const StyledSelectiveButton = styled.button<{
  isActive: boolean;
  variant: SelectiveButtonProps['variant'];
  size: SelectiveButtonProps['size'];
}>`
  background: transparent;
  box-sizing: border-box;
  color: ${({ theme }) => theme.Color.Neutrals.Dark};
  border-radius: ${({ theme }) => rem(theme.Border.Radius.Medium)};
  border: 1px solid
    ${({ theme, isActive }) => (isActive ? theme.Color.Brand.Primary : theme.Color.Neutrals.Border)};
  cursor: pointer;
  display: inline-flex;
  flex: 1;
  justify-content: center;
  align-items: center;
  ${getFontStyle('Small')}
  padding: ${({ theme }) => `${rem(theme.Spacing.Size.Small)}`};
  user-select: none;
  transition: all ${({ theme }) => theme.Transition.Timing.Out};
  ${({ variant, size }) => getSelectiveButtonSize({ variant, size })};

  &.disabled {
    pointer-events: none;
  }
`;

export const StyledBadge = styled.button<{
  position?: Position;
  size?: Size;
}>`
  position: absolute;
  ${({ position, size }) => getBadgePosition({ position, size })};
  background: ${({ theme }) => theme.Color.Neutrals.Paper};
  height: ${({ theme }) => `${rem(theme.Icon.Size.XSmall)}`};
  width: ${({ theme }) => `${rem(theme.Icon.Size.XSmall)}`};
  border-radius: ${({ theme }) => `${rem(theme.Border.Radius.Large)}`};
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${({ theme }) => theme.Color.Feedback.Alert};
  padding: ${({ theme }) => `${rem(theme.Spacing.Size.XXSmall)}`};
  cursor: pointer;
`;
